/** Typed HTTP client for the Formency API with cookie auth, envelope unwrapping, and NDJSON streaming. */
import axios from 'axios';
import { toast } from 'sonner';
import { handleSessionExpired } from './sessionExpired';
import { isSigningOut as _isSigningOutFlag } from './signOutFlag';

// ─── Base URL ───────────────────────────────────────────────────────────────

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

// ─── Axios instance ─────────────────────────────────────────────────────────
// withCredentials: true is REQUIRED for BetterAuth cookies to flow on
// cross-subdomain requests (e.g. formency.app → api.formency.app). The
// browser still respects the cookie's SameSite/Secure/Domain attributes —
// this just opts the SPA in to sending them.

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ─── Sign-out flag (re-exported from signOutFlag.ts) ────────────────────────
// Living in its own module breaks the circular dep with sessionExpired.ts.

export { setSigningOut, isSigningOut } from './signOutFlag';

let _connectionToastActive = false;

const CONNECTION_TOAST_ID = 'connection' as const;

// ─── Timezone ───────────────────────────────────────────────────────────────

let _userTimezone: string | null = null;
export function setUserTimezone(tz: string | null) {
  _userTimezone = tz;
}

// ─── Interceptors ───────────────────────────────────────────────────────────
// No Authorization header injection — BetterAuth's HttpOnly cookies are
// sent automatically by the browser when withCredentials is true. The
// previous Supabase Bearer logic was removed in #36 Task A.

apiClient.interceptors.request.use(
  (config) => {
    config.headers['X-Timezone'] =
      _userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    if (_connectionToastActive) {
      _connectionToastActive = false;
      toast.success('Back online', { id: CONNECTION_TOAST_ID, duration: 3000 });
    }

    const data = response.data;
    if (data?.success && data?.pagination) {
      return {
        data: data.data,
        pagination: {
          ...data.pagination,
          totalPages: Math.ceil(data.pagination.total / data.pagination.pageSize),
        },
      };
    }
    if (data?.success && data?.data !== undefined) {
      return data.data;
    }
    return data;
  },
  (error) => {
    const url = error.config?.url ?? '';
    // Skip 401-redirect for /api/auth/* — those are sign-in attempts, OTP
    // verifications, etc. A bad password returns 401 and we don't want THAT
    // to bounce the user. The auth pages handle those errors themselves.
    const isAuthEndpoint = url.includes('/auth/');

    if (error.response?.status === 401 && !_isSigningOutFlag() && !isAuthEndpoint) {
      handleSessionExpired();
    } else if (
      (!error.response || error.response?.status === 503) &&
      !_isSigningOutFlag() &&
      !_connectionToastActive
    ) {
      _connectionToastActive = true;
      toast.loading('Connection lost — reconnecting...', {
        id: CONNECTION_TOAST_ID,
        duration: Infinity,
      });
    }
    return Promise.reject(error);
  }
);

// ─── Typed request wrappers ─────────────────────────────────────────────────

export function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  return apiClient.get(url, { params }) as unknown as Promise<T>;
}

export function post<T>(url: string, data?: unknown, config?: Record<string, unknown>): Promise<T> {
  return apiClient.post(url, data, config) as unknown as Promise<T>;
}

export function put<T>(url: string, data?: unknown): Promise<T> {
  return apiClient.put(url, data) as unknown as Promise<T>;
}

export function patch<T>(url: string, data?: unknown): Promise<T> {
  return apiClient.patch(url, data) as unknown as Promise<T>;
}

export function del<T = void>(url: string, config?: Record<string, unknown>): Promise<T> {
  return apiClient.delete(url, config) as unknown as Promise<T>;
}

// ─── NDJSON stream reader (centralised in @/shared/utils/streamNdjson) ────

import { streamNdjson } from '@/shared/utils/streamNdjson';
export { streamNdjson, type StreamNdjsonOptions } from '@/shared/utils/streamNdjson';

// ─── Convenience namespace ──────────────────────────────────────────────────
// Features define their own API calls using the typed wrappers above.
// See v1 src/shared/api/client.ts for reference implementations.

export const api = { get, post, put, patch, del, stream: streamNdjson };
